import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Upload as UploadIcon, Download, FileSpreadsheet, CheckCircle, AlertCircle, 
  ChevronDown, ChevronRight, AlertTriangle, RefreshCw, Calendar
} from 'lucide-react';

const DataExchange = () => {
  // =============================================
  // UPLOAD STATE
  // =============================================
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mappingExpanded, setMappingExpanded] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // =============================================
  // DOWNLOAD STATE
  // =============================================
  const [exportStatus, setExportStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  // =============================================
  // LOAD DATA ON MOUNT
  // =============================================
  useEffect(() => {
    fetchMapping();
    fetchExportStatus();
  }, []);

  // =============================================
  // UPLOAD FUNCTIONS
  // =============================================
  const fetchMapping = async () => {
    try {
      const response = await api.get('/upload/mapping');
      setMapping(response.data.mapping || {});
    } catch (err) {
      console.error('Failed to fetch mapping:', err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadResult(null);
      setDuplicateInfo(null);
    }
  };

  const checkForDuplicate = async () => {
    if (!file) return;
    
    setCheckingDuplicate(true);
    try {
      const response = await api.post('/upload/check-duplicate', {
        fileName: file.name
      });
      
      if (response.data.isDuplicate || response.data.hasOtherUploadsThisWeek) {
        setDuplicateInfo(response.data);
        setShowDuplicateWarning(true);
      } else {
        await performUpload();
      }
    } catch (err) {
      console.error('Failed to check duplicate:', err);
      await performUpload();
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const performUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    setShowDuplicateWarning(false);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldMapping', JSON.stringify(mapping));

      const response = await api.post('/upload/qad', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult({
        success: true,
        totalRecords: response.data.batch?.totalRecords || 0,
        created: response.data.batch?.created || 0,
        updated: response.data.batch?.updated || 0,
        errorCount: response.data.batch?.errors || 0,
        errorList: response.data.errors || []
      });
      
      setFile(null);
      setDuplicateInfo(null);
      // Refresh export status to show new batch
      fetchExportStatus();
    } catch (err) {
      setUploadResult({
        success: false,
        error: err.response?.data?.error || 'Upload failed'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    await checkForDuplicate();
  };

  // =============================================
  // DOWNLOAD FUNCTIONS
  // =============================================
  const fetchExportStatus = async (batchId = null) => {
    setLoadingStatus(true);
    try {
      const url = batchId ? `/export/status?batchId=${batchId}` : '/export/status';
      const response = await api.get(url);
      setExportStatus(response.data);
      if (!selectedBatchId && response.data.batch) {
        setSelectedBatchId(response.data.batch.id.toString());
      }
    } catch (err) {
      console.error('Failed to fetch export status:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatchId(batchId);
    if (batchId) {
      fetchExportStatus(batchId);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = selectedBatchId 
        ? `/export/download?batchId=${selectedBatchId}`
        : '/export/download';
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'PO_Status_Export.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      alert(`Download failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // =============================================
  // HELPERS
  // =============================================
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div>
      <h1 className="mb-6" style={{ fontSize: '1.5rem', fontWeight: '600' }}>Data Exchange</h1>

      {/* =============================================
          UPLOAD SECTION
          ============================================= */}
      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <UploadIcon size={20} />
            <h2 className="card-title" style={{ margin: 0 }}>Upload PO Data</h2>
          </div>
          <span className="text-sm text-muted">Import QAD weekly report</span>
        </div>
        <div className="card-body">
          <div
            style={{
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '32px',
              textAlign: 'center',
              background: file ? 'var(--bg-secondary)' : 'transparent'
            }}
          >
            {file ? (
              <div>
                <FileSpreadsheet size={40} style={{ color: 'var(--primary)', marginBottom: '12px' }} />
                <div className="font-medium">{file.name}</div>
                <div className="text-sm text-muted mb-4">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
                <div className="flex justify-center gap-4">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setFile(null); setDuplicateInfo(null); }}>
                    Remove
                  </button>
                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={handleUpload}
                    disabled={uploading || checkingDuplicate}
                  >
                    <UploadIcon size={14} />
                    {checkingDuplicate ? 'Checking...' : uploading ? 'Uploading...' : 'Upload & Process'}
                  </button>
                </div>
              </div>
            ) : (
              <label style={{ cursor: 'pointer' }}>
                <UploadIcon size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
                <div className="font-medium mb-2">Drop your file here or click to browse</div>
                <div className="text-sm text-muted">Supports .xlsx, .xls, .csv files</div>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div 
              className={`alert ${uploadResult.success ? (uploadResult.errorCount > 0 ? 'alert-warning' : 'alert-success') : 'alert-error'}`} 
              style={{ marginTop: '16px' }}
            >
              {uploadResult.success ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {uploadResult.errorCount > 0 ? (
                      <>
                        <AlertTriangle size={18} />
                        <strong>Upload Completed with Errors</strong>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        <strong>Upload Successful</strong>
                      </>
                    )}
                  </div>
                  <div className="text-sm">
                    Total: {uploadResult.totalRecords} • Created: {uploadResult.created} • Updated: {uploadResult.updated} • Errors: {uploadResult.errorCount}
                  </div>
                  {uploadResult.errorList && uploadResult.errorList.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(0,0,0,0.05)', borderRadius: 'var(--radius)' }}>
                      <div className="font-medium mb-2">Row Errors:</div>
                      {uploadResult.errorList.slice(0, 5).map((err, idx) => (
                        <div key={idx} className="text-sm" style={{ marginBottom: '4px' }}>
                          Row {err.row}: {err.error}
                        </div>
                      ))}
                      {uploadResult.errorList.length > 5 && (
                        <div className="text-sm text-muted">...and {uploadResult.errorList.length - 5} more</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span>{uploadResult.error}</span>
                </div>
              )}
            </div>
          )}

          {/* Field Mapping - Collapsible */}
          <div style={{ marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
            <div 
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => setMappingExpanded(!mappingExpanded)}
            >
              <div className="flex items-center gap-2">
                {mappingExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="font-medium">Field Mapping</span>
              </div>
              <span className="text-sm text-muted">{Object.keys(mapping).length} fields</span>
            </div>
            {mappingExpanded && (
              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '8px' }}>
                {Object.entries(mapping).map(([dbField, excelColumn]) => (
                  <div key={dbField} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}>
                    <span className="text-muted">{dbField.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{excelColumn}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* =============================================
          DOWNLOAD SECTION
          ============================================= */}
      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Download size={20} />
            <h2 className="card-title" style={{ margin: 0 }}>Download Status Report</h2>
          </div>
          <button 
            className="btn btn-outline btn-sm" 
            onClick={() => fetchExportStatus(selectedBatchId || null)}
            disabled={loadingStatus}
          >
            <RefreshCw size={14} className={loadingStatus ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
        <div className="card-body">
          {loadingStatus && !exportStatus ? (
            <div className="text-center text-muted" style={{ padding: '20px' }}>
              Loading...
            </div>
          ) : (
            <>
              {/* Batch Selector - Always show */}
              <div style={{ marginBottom: '20px' }}>
                <label className="text-sm font-medium" style={{ display: 'block', marginBottom: '6px' }}>
                  Select Batch
                </label>
                {exportStatus?.batches?.length > 0 ? (
                  <select 
                    className="form-control" 
                    value={selectedBatchId}
                    onChange={handleBatchChange}
                    style={{ maxWidth: '400px' }}
                  >
                    {exportStatus.batches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.fileName} - Week {batch.week}/{batch.year} ({formatShortDate(batch.uploadedAt)})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-muted text-sm" style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', maxWidth: '400px' }}>
                    No batches available. Upload a file first.
                  </div>
                )}
              </div>

              {/* Batch Info - Show only if batch selected */}
              {exportStatus?.batch ? (
                <>
                  {/* Current Batch Info */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                    gap: '16px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius)' }}>
                      <div className="text-sm text-muted">Batch</div>
                      <div className="font-medium">#{exportStatus.batch.id}</div>
                    </div>
                    <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius)' }}>
                      <div className="text-sm text-muted">Week / Year</div>
                      <div className="font-medium flex items-center gap-1">
                        <Calendar size={14} />
                        Week {exportStatus.batch.week}, {exportStatus.batch.year}
                      </div>
                    </div>
                    <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius)' }}>
                      <div className="text-sm text-muted">Total PO Lines</div>
                      <div className="font-medium">{exportStatus.total}</div>
                    </div>
                    <div style={{ background: '#d4edda', padding: '16px', borderRadius: 'var(--radius)' }}>
                      <div className="text-sm" style={{ color: '#155724' }}>Processed</div>
                      <div className="font-medium" style={{ color: '#155724' }}>{exportStatus.withAction}</div>
                    </div>
                    <div style={{ background: '#fff3cd', padding: '16px', borderRadius: 'var(--radius)' }}>
                      <div className="text-sm" style={{ color: '#856404' }}>Pending</div>
                      <div className="font-medium" style={{ color: '#856404' }}>{exportStatus.pending}</div>
                    </div>
                  </div>

                  {/* Status Breakdown */}
                  {exportStatus.byStatus && Object.keys(exportStatus.byStatus).length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div className="text-sm font-medium mb-2">By Response Status</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(exportStatus.byStatus).map(([status, count]) => (
                          <span key={status} className="badge badge-secondary">
                            {status}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Breakdown */}
                  {exportStatus.byAction && Object.keys(exportStatus.byAction).length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div className="text-sm font-medium mb-2">By Action Type</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(exportStatus.byAction).map(([action, count]) => {
                          const badgeClass = action === 'POSITIVE' ? 'badge-success' :
                                             action === 'DELAY' ? 'badge-warning' :
                                             action === 'SHORT_CLOSE' ? 'badge-danger' : 'badge-secondary';
                          return (
                            <span key={action} className={`badge ${badgeClass}`}>
                              {action}: {count}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Download Button */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                    <button 
                      className="btn btn-primary"
                      onClick={handleDownload}
                      disabled={downloading}
                    >
                      {downloading ? (
                        <>
                          <RefreshCw size={16} className="animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download size={16} />
                          Download Excel Report
                        </>
                      )}
                    </button>
                    <div className="text-sm text-muted" style={{ marginTop: '8px' }}>
                      Includes all PO lines with latest action details, AI summaries, and processing info
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted" style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)' }}>
                  Select a batch to view details and download report.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* =============================================
          DUPLICATE WARNING MODAL
          ============================================= */}
      {showDuplicateWarning && duplicateInfo && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <div className="card-header" style={{ background: 'var(--warning)', color: 'white' }}>
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} />
                <h3 className="card-title" style={{ margin: 0, color: 'white' }}>
                  {duplicateInfo.isDuplicate ? 'Duplicate File Detected' : 'Existing Data This Week'}
                </h3>
              </div>
            </div>
            <div className="card-body">
              {duplicateInfo.isDuplicate ? (
                <div>
                  <p className="mb-4">
                    You already uploaded <strong>"{duplicateInfo.previousUpload.fileName}"</strong> this week.
                  </p>
                  <div style={{ 
                    background: 'var(--bg-secondary)', 
                    padding: '16px', 
                    borderRadius: 'var(--radius)',
                    marginBottom: '16px'
                  }}>
                    <div className="text-sm mb-2">
                      <strong>Previous Upload:</strong>
                    </div>
                    <div className="text-sm text-muted">
                      Date: {formatDate(duplicateInfo.previousUpload.uploadedAt)}
                    </div>
                    <div className="text-sm text-muted">
                      Records: {duplicateInfo.previousUpload.totalRecords} total 
                      ({duplicateInfo.previousUpload.createdCount} created, {duplicateInfo.previousUpload.updatedCount} updated)
                    </div>
                    <div className="text-sm text-muted">
                      Status: {duplicateInfo.previousUpload.status}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mb-4">
                    There's already data uploaded this week from a different file.
                  </p>
                  <div style={{ 
                    background: 'var(--bg-secondary)', 
                    padding: '16px', 
                    borderRadius: 'var(--radius)',
                    marginBottom: '16px'
                  }}>
                    <div className="text-sm text-muted">
                      Last upload: {duplicateInfo.lastUpload?.fileName}
                    </div>
                    <div className="text-sm text-muted">
                      Date: {formatDate(duplicateInfo.lastUpload?.uploadedAt)}
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107',
                padding: '12px', 
                borderRadius: 'var(--radius)',
                marginBottom: '16px'
              }}>
                <div className="text-sm">
                  <strong>⚠️ {duplicateInfo.existingRecordsThisWeek || 0}</strong> PO lines exist for this week.
                </div>
                <div className="text-sm" style={{ marginTop: '4px' }}>
                  Uploading again will <strong>UPDATE</strong> existing records (matching PO# + Line#) and create new ones.
                </div>
              </div>
            </div>
            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid var(--border)', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px' 
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDuplicateWarning(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={performUpload}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Continue Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataExchange;